import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { Hero, Marquee } from "@/components/home/Hero";
import { ServicesBento, MissionSplit } from "@/components/home/HomeSections";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { TrustBadges } from "@/components/common/TrustBadges";
import { InstagramBlock } from "@/components/common/InstagramBlock";
import { TestimonialCard } from "@/components/common/TestimonialCard";
import { ChampSection } from "@/components/common/ChampSection";

const AcademyTeaser = () => (
  <section className="relative bg-[#2E0F3E] py-24 md:py-32 overflow-hidden" data-testid="home-academy">
    <img src="/images/office.jpg" srcSet="/images/office-m.jpg 900w, /images/office.jpg 1400w" sizes="100vw" loading="lazy" alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-[#2E0F3E]/70" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
      <div className="md:col-span-8">
        <GraduationCap className="w-12 h-12 text-[#D4AF37] mb-8" strokeWidth={1.2} />
        <SectionHeading eyebrow="B2R Financial Freedom Academy"
          title={<>Knowledge is the <span className="italic gold-text">golden ticket.</span></>}
          sub="Classes, workshops and masterclasses on financial literacy, credit, and building a business — designed to make wealth-building feel achievable." />
      </div>
      <Reveal className="md:col-span-4 md:justify-self-end" delay={0.2}>
        <Link to="/academy" className="btn-gold" data-testid="home-academy-link">View Upcoming Classes</Link>
      </Reveal>
    </div>
  </section>
);

const TestimonialsPreview = () => {
  const { data = [] } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then((r) => r.data) });
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32" data-testid="home-testimonials">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading eyebrow="Client Stories" title={<>Real people. <span className="italic gold-text">Real results.</span></>} />
        {data.length ? (
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {data.slice(0, 3).map((t, i) => <Reveal key={t.id} delay={i * 0.1}><TestimonialCard t={t} /></Reveal>)}
          </div>
        ) : (
          <Reveal className="mt-14 lux-card p-10 md:p-14 flex flex-col md:flex-row gap-8 justify-between md:items-center">
            <p className="font-display text-2xl md:text-3xl text-white/85 max-w-2xl">Worked with B2R? Your story could inspire someone's first step toward financial freedom.</p>
            <Link to="/testimonials" className="btn-outline-gold shrink-0" data-testid="home-share-story-link">Share Your Story</Link>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const FinalCta = () => (
  <section className="bg-black py-24 md:py-32" data-testid="home-final-cta">
    <div className="max-w-5xl mx-auto px-6 md:px-12 text-left md:text-center">
      <Reveal>
        <p className="eyebrow mb-6">Your Next Move</p>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight">Ready to turn your bag into <span className="gold-text italic">riches?</span></h2>
        <div className="mt-12 flex flex-col sm:flex-row gap-4 md:justify-center">
          <Link to="/book" className="btn-gold" data-testid="final-cta-book">Book a Consultation</Link>
          <Link to="/contact" className="btn-glass" data-testid="final-cta-contact">Ask a Question</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <ServicesBento />
      <MissionSplit />
      <ChampSection />
      <AcademyTeaser />
      <TestimonialsPreview />
      <section className="bg-[#0A0A0A] pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Trust & Security" title="Your finances, handled with care." className="mb-14" />
          <TrustBadges />
        </div>
      </section>
      <InstagramBlock />
      <FinalCta />
    </div>
  );
}
